import {
    type ManagedPlaylistController,
    PlaylistControllerImpl,
} from '@/playback/PlaylistController'
import { logDebug } from '@amazon/vinyl-util'
import {
    createContainer,
    type Container,
    type Factories,
    validateFactories,
} from '@amazon/vinyl-di'
import { createVinylPlayer, initializePlayerAuthBinding } from '@/playback/player'
import {
    type CloudQueueControllerDeps,
    CloudQueueControllerImpl,
} from '@/cloudQueue/CloudQueueController'
import { BridgedCloudQueueController } from '@/cloudQueue/BridgedCloudQueueController'
import { getCastingBridge } from '@/casting/CastingBridge'
import type { AppViewDeps } from '@/AppView'
import { BridgedPlaylistController } from '@/playback/BridgedPlaylistController'
import { createSubCommandBridge } from '@/bridge/CommandBridge'
import type { Player } from '@/playback/api'
import { isDesktop, isMobile, isTv } from '@/device/deviceConfig'
import type { BridgeDelegateOptions } from '@/bridge/BridgeDelegate'
import { BridgedPlayer } from '@/playback/BridgedPlayer'
import { ctx } from '@/util/context'
import { bindStreamingQualityToPlayer } from '@/streamingQuality/streamingQualityPlayerBinding'
import { getStreamingSettingsController } from '@/streamingQuality/StreamingSettingsController'
import { createGoodNetworkObservable } from '@/offline/createGoodNetworkObservable'
import {
    PartySessionControllerImpl,
    type PartySessionControllerImplDeps,
} from '@/party/PartySessionController'

/**
 * Interface for application dependencies with lazy-loaded player
 */
export type AppDependencies = AppViewDeps
const { target } = ctx('appDependencies')

export function createAppDependencies(): Container<AppDependencies> {
    const castingBridge = getCastingBridge()

    const castingOptions = {
        initiator: isDesktop() || isMobile(),
        responder: isTv(),
    } as const satisfies Partial<BridgeDelegateOptions<any>>

    logDebug(target, 'create, cast mode:', castingBridge.canSend())

    return createContainer(
        validateFactories({
            playerPromise: async (_, add): Promise<Player> => {
                const player = await createVinylPlayer()
                add(initializePlayerAuthBinding(player))
                add(bindStreamingQualityToPlayer(getStreamingSettingsController(), player))

                return castingBridge.canSend()
                    ? new BridgedPlayer({
                          localController: player,
                          bridge: createSubCommandBridge(castingBridge, 'player'),
                          ...castingOptions,
                      })
                    : player
            },

            playlistController: (): ManagedPlaylistController => {
                const localController = new PlaylistControllerImpl()
                return castingBridge.canSend()
                    ? new BridgedPlaylistController({
                          localController,
                          bridge: createSubCommandBridge(castingBridge, 'playlist'),
                          ...castingOptions,
                      })
                    : localController
            },

            cloudQueueController: (deps: CloudQueueControllerDeps) => {
                const localController = new CloudQueueControllerImpl(deps)
                return castingBridge.canSend()
                    ? new BridgedCloudQueueController({
                          localController,
                          bridge: createSubCommandBridge(castingBridge, 'cloudQueue'),
                          ...castingOptions,
                      })
                    : localController
            },

            goodNetwork: createGoodNetworkObservable,

            partySessionController: (deps: PartySessionControllerImplDeps) =>
                new PartySessionControllerImpl(deps),
        } as const satisfies Factories<AppViewDeps>)
    )
}
